import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit3, X, Crown, Sparkles } from "lucide-react";

const RARITIES = [
  { value: "common", label: "Common", color: "text-zinc-400 border-zinc-600" },
  { value: "rare", label: "Rare", color: "text-blue-400 border-blue-600" },
  { value: "epic", label: "Epic", color: "text-purple-400 border-purple-600" },
  { value: "legendary", label: "Legendary", color: "text-orange-400 border-orange-600" },
  { value: "mythic", label: "Mythic", color: "text-pink-400 border-pink-600" },
];

const emptyFrame = () => ({
  slug: "", name: "", description: "", image_url: "",
  rarity: "common", price_points: 100, required_role: "user", enabled: true, stock: -1, order: 0,
});

export default function IDGMarketplaceTab() {
  const [frames, setFrames] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/idg/admin/marketplace/frames").then((r) => setFrames(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/idg/admin/marketplace/frames/${editing.id}`, editing);
      else await api.post("/idg/admin/marketplace/frames", editing);
      toast.success("Cadru salvat"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur ștergi acest cadru? Tranzacțiile rămân.")) return;
    await api.delete(`/idg/admin/marketplace/frames/${id}`);
    toast.success("Șters"); load();
  };

  return (
    <div className="space-y-4" data-testid="idg-marketplace-tab">
      {!editing ? (
        <>
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title flex items-center gap-2"><Crown className="w-3.5 h-3.5 text-[var(--tf-primary)]" /> VIP Frame Marketplace</h3>
            <button onClick={() => setEditing(emptyFrame())} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1" data-testid="idg-frame-new">
              <Plus className="w-3.5 h-3.5" /> Cadru nou
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {frames.map((f) => {
              const rarity = RARITIES.find((r) => r.value === f.rarity) || RARITIES[0];
              return (
                <div key={f.id} className={`tf-surface tf-clip-card p-4 border ${rarity.color}`} data-testid={`idg-frame-${f.id}`}>
                  <div className="aspect-square bg-cover bg-center rounded-sm tf-clip-card-sm mb-3" style={{ backgroundImage: f.image_url ? `url(${f.image_url})` : undefined }}>
                    {!f.image_url && <div className="h-full flex items-center justify-center text-zinc-600"><Sparkles className="w-8 h-8" /></div>}
                  </div>
                  <div className={`text-[9px] tf-section-title ${rarity.color}`}>{rarity.label}</div>
                  <div className="tf-heading font-bold text-white truncate">{f.name}</div>
                  <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{f.description}</div>
                  <div className="flex items-center justify-between mt-3 text-xs tf-mono">
                    <span className="text-[var(--tf-primary)] font-bold">{f.price_points} pts</span>
                    <span className="text-zinc-500">{f.sales_count || 0} vândute</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {!f.enabled && <span className="text-[9px] px-1.5 py-0.5 bg-zinc-700 text-white tf-clip-card-sm">DEZACTIVAT</span>}
                    {f.required_role === "vip" && <span className="text-[9px] px-1.5 py-0.5 bg-[var(--tf-primary)] text-black tf-clip-card-sm font-bold">VIP</span>}
                  </div>
                  <div className="flex gap-1 mt-3 pt-3 border-t border-white/5">
                    <button onClick={() => setEditing(f)} className="flex-1 tf-btn-secondary tf-clip-btn text-[10px] flex items-center justify-center gap-1" data-testid={`idg-frame-edit-${f.id}`}>
                      <Edit3 className="w-3 h-3" /> Editează
                    </button>
                    <button onClick={() => remove(f.id)} className="text-red-500 hover:bg-red-500/10 p-1.5 tf-clip-card-sm" data-testid={`idg-frame-delete-${f.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="tf-surface tf-clip-card p-5 space-y-3" data-testid="idg-frame-form">
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title">{editing.id ? "Editează cadru" : "Cadru nou"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="frame-slug" />
            <Field label="Nume" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="frame-name" />
            <Field label="Image URL" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testid="frame-image" />
            <div>
              <label className="text-xs tf-section-title block mb-1">Raritate</label>
              <select className="tf-input" value={editing.rarity} onChange={(e) => setEditing({ ...editing, rarity: e.target.value })} data-testid="frame-rarity">
                {RARITIES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <Field label="Preț (puncte)" type="number" value={editing.price_points} onChange={(v) => setEditing({ ...editing, price_points: parseInt(v) || 0 })} testid="frame-price" />
            <Field label="Stock (-1 = nelimitat)" type="number" value={editing.stock ?? -1} onChange={(v) => setEditing({ ...editing, stock: parseInt(v) || -1 })} testid="frame-stock" />
            <div>
              <label className="text-xs tf-section-title block mb-1">Rol necesar</label>
              <select className="tf-input" value={editing.required_role} onChange={(e) => setEditing({ ...editing, required_role: e.target.value })} data-testid="frame-required-role">
                <option value="user">Toți</option>
                <option value="vip">Doar VIP</option>
              </select>
            </div>
            <Field label="Ordine afișare" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} testid="frame-order" />
          </div>
          <Field label="Descriere" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} textarea testid="frame-description" />
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} data-testid="frame-enabled" />
            Activat (apare în marketplace)
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={save} className="tf-btn-primary tf-clip-btn flex items-center gap-1" data-testid="frame-save"><Save className="w-3.5 h-3.5" /> Salvează</button>
            <button onClick={() => setEditing(null)} className="tf-btn-secondary tf-clip-btn">Anulează</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      {textarea ? <textarea className="tf-input" rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      : <input type={type} className="tf-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
    </div>
  );
}
